import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Button,
  Text,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Input,
  Spinner,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Field,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Select,
  Option,
  Switch,
  Checkbox,
  makeStyles,
} from '@fluentui/react-components';
import {
  Add24Regular,
  Search24Regular,
  Person24Regular,
  MoreHorizontal24Regular,
  Edit24Regular,
  Delete24Regular,
  PersonDelete24Regular,
  DocumentText24Regular,
  Dismiss24Regular,
} from '@fluentui/react-icons';
import { usersApi, surveysApi, assignmentsApi } from '../services/api.ts';
import PageHeader from '../components/PageHeader.tsx';
import StatusBadge from '../components/StatusBadge.tsx';
import EmptyState from '../components/EmptyState.tsx';
import ConfirmDialog from '../components/ConfirmDialog.tsx';
import DeleteUserDialog from '../components/DeleteUserDialog.tsx';
import Pagination from '../components/Pagination.tsx';
import { useToast } from '../components/Toast.tsx';

const getTimezones = (): string[] => {
  const intlAny = Intl as any;
  if (typeof intlAny.supportedValuesOf === 'function') {
    try {
      return intlAny.supportedValuesOf('timeZone');
    } catch {
      // fall through to default
    }
  }
  return ['UTC'];
};

const TIMEZONES = getTimezones();

// Matched against the free-text Country field, which is often written as
// "City, Country". Without this a new user silently inherits the timezone of
// whichever admin created them, and their reminders arrive hours off.
const COUNTRY_TIMEZONES: Array<[string, string]> = [
  ['armenia', 'Asia/Yerevan'],
  ['georgia', 'Asia/Tbilisi'],
  ['ukraine', 'Europe/Kyiv'],
  ['moldova', 'Europe/Chisinau'],
  ['turkiye', 'Europe/Istanbul'],
  ['turkey', 'Europe/Istanbul'],
  ['serbia', 'Europe/Belgrade'],
  ['montenegro', 'Europe/Podgorica'],
  ['bulgaria', 'Europe/Sofia'],
  ['romania', 'Europe/Bucharest'],
  ['finland', 'Europe/Helsinki'],
  ['germany', 'Europe/Berlin'],
  ['italy', 'Europe/Rome'],
  ['india', 'Asia/Kolkata'],
  ['south korea', 'Asia/Seoul'],
  ['united kingdom', 'Europe/London'],
];

const countryToTimezone = (country: string): string | null => {
  const low = country.trim().toLowerCase();
  if (!low) return null;
  const hit = COUNTRY_TIMEZONES.find(([name]) => low.includes(name));
  return hit ? hit[1] : null;
};

const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const MOBILE = '@media (max-width: 768px)';

const useStyles = makeStyles({
  card: {
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-md)',
    padding: 'var(--space-lg)',
    marginBottom: 'var(--space-lg)',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 'var(--space-md)',
    flexWrap: 'wrap',
    marginBottom: 'var(--space-md)',
  },
  bulkBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 'var(--space-md)',
    flexWrap: 'wrap',
    padding: 'var(--space-sm) var(--space-md)',
    marginBottom: 'var(--space-md)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--undp-primary)',
    backgroundColor: 'var(--color-background)',
  },
  bulkActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-sm)',
    flexWrap: 'wrap',
  },
  searchWrap: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    maxWidth: '300px',
  },
  searchIcon: {
    position: 'absolute',
    left: '10px',
    color: 'var(--color-text-tertiary)',
    pointerEvents: 'none',
  },
  counters: {
    display: 'flex',
    gap: 'var(--space-lg)',
    alignItems: 'center',
  },
  counter: {
    textAlign: 'center',
  },
  counterValue: {
    fontSize: '18px',
    fontWeight: '600',
    lineHeight: '1',
    display: 'block',
  },
  counterLabel: {
    fontSize: '11px',
    color: 'var(--color-text-secondary)',
    display: 'block',
    marginTop: '2px',
  },
  infoBlock: {
    padding: 'var(--space-sm) var(--space-md)',
    backgroundColor: 'var(--color-background)',
    borderRadius: 'var(--radius-sm)',
    marginTop: 'var(--space-md)',
  },
  infoRow: {
    fontSize: '12px',
    color: 'var(--color-text-secondary)',
    lineHeight: '1.8',
  },
  table: {
    minWidth: '900px',
    // On mobile we drop the extra columns, so the table fits with no scroll.
    [MOBILE]: {
      minWidth: 0,
    },
    // Fluent's Table uses table-layout:fixed with equal columns and no overflow
    // handling, so long unbreakable tokens (emails) spill into the next column.
    // Keep every cell on a single line and truncate overflow with an ellipsis.
    '& th, & td': {
      paddingRight: 'var(--space-lg)',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      verticalAlign: 'middle',
    },
    '& th:last-child, & td:last-child': {
      paddingRight: 'var(--space-sm)',
    },
  },
  hideOnMobile: {
    [MOBILE]: {
      display: 'none',
    },
  },
  nameCell: {
    [MOBILE]: {
      cursor: 'pointer',
    },
  },
  expandHint: {
    display: 'none',
    [MOBILE]: {
      display: 'inline',
      marginLeft: 'var(--space-xs)',
      color: 'var(--color-text-tertiary)',
      fontSize: '12px',
    },
  },
  // Mobile-only popup (blurred backdrop + centered panel). Hidden on desktop.
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--space-lg)',
    zIndex: 1000,
    animationName: { from: { opacity: 0 }, to: { opacity: 1 } },
    animationDuration: '0.18s',
    '@media (min-width: 769px)': {
      display: 'none',
    },
  },
  modalPanel: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-lg)',
    padding: 'var(--space-lg)',
    width: '100%',
    maxWidth: '420px',
    maxHeight: '85vh',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-md)',
    animationName: {
      from: { opacity: 0, transform: 'scale(0.96)' },
      to: { opacity: 1, transform: 'scale(1)' },
    },
    animationDuration: '0.2s',
    animationTimingFunction: 'ease',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 'var(--space-sm)',
  },
  modalActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-sm)',
  },
  detailMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-xs)',
    fontSize: '13px',
    color: 'var(--color-text-secondary)',
  },
});

function useIsMobile(): boolean {
  const query = '(max-width: 768px)';
  const [isMobile, setIsMobile] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

export default function Users() {
  const s = useStyles();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [mobileUser, setMobileUser] = useState<any>(null);
  const isMobile = useIsMobile();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newUserFirstName, setNewUserFirstName] = useState('');
  const [newUserLastName, setNewUserLastName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserCountry, setNewUserCountry] = useState('');
  const [newUserTimezone, setNewUserTimezone] = useState(getBrowserTimezone());
  const [isCreating, setIsCreating] = useState(false);

  const [showManageDialog, setShowManageDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [editUserFirstName, setEditUserFirstName] = useState('');
  const [editUserLastName, setEditUserLastName] = useState('');
  const [editUserEmail, setEditUserEmail] = useState('');
  const [editUserCountry, setEditUserCountry] = useState('');
  const [editUserTimezone, setEditUserTimezone] = useState('UTC');
  const [editAutoReminder, setEditAutoReminder] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkDays, setBulkDays] = useState('7');
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [editAutoReminderDays, setEditAutoReminderDays] = useState('7');
  const [isUpdating, setIsUpdating] = useState(false);

  const [confirmAction, setConfirmAction] = useState<{ type: string; user: any } | null>(null);
  const [userToDelete, setUserToDelete] = useState<any | null>(null);

  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedUserForAssignment, setSelectedUserForAssignment] = useState<any>(null);
  const [selectedSurveyForAssignment, setSelectedSurveyForAssignment] = useState<number | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(id);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, pageSize]);

  const { data: usersPage, isLoading } = useQuery(
    ['users', page, pageSize, debouncedSearch],
    () => usersApi.list({
      skip: (page - 1) * pageSize,
      limit: pageSize,
      search: debouncedSearch || undefined,
    }),
    { keepPreviousData: true }
  );
  const users = usersPage?.items ?? [];
  const totalUsers = usersPage?.total ?? 0;
  const allOnPageSelected = users.length > 0 && users.every((u) => selectedIds.includes(u.id));

  // Selection is per page. Carrying it across a page or search change would let
  // an admin act on rows they can no longer see.
  useEffect(() => { setSelectedIds([]); }, [page, pageSize, debouncedSearch]);

  // If the current page falls off the end (e.g. after deletes), step back to the new last page.
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(totalUsers / pageSize));
    if (page > maxPage) setPage(maxPage);
  }, [totalUsers, pageSize, page]);
  const { data: availableSurveys, refetch: refetchAvailableSurveys } = useQuery(
    'available-surveys',
    () => surveysApi.getAvailableForAssignment(),
    { staleTime: 0, cacheTime: 0 }
  );

  const createUserMutation = useMutation(usersApi.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      setShowAddDialog(false);
      setNewUserFirstName(''); setNewUserLastName(''); setNewUserEmail(''); setNewUserCountry(''); setNewUserTimezone(getBrowserTimezone());
      toast.success('User created successfully');
    },
    onError: () => toast.error('Failed to create user'),
  });

  const updateUserMutation = useMutation(
    ({ id, data }: { id: number; data: any }) => usersApi.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        setShowManageDialog(false); setSelectedUser(null);
        toast.success('User updated successfully');
      },
      onError: () => toast.error('Failed to update user'),
    }
  );

  const updateUserStatusMutation = useMutation(
    ({ id, status }: { id: number; status: string }) => usersApi.updateStatus(id, status),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        setShowManageDialog(false); setSelectedUser(null);
        toast.success('User status updated');
      },
      onError: () => toast.error('Failed to update user status'),
    }
  );

  const deleteUserMutation = useMutation(
    (id: number) => usersApi.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users']);
        queryClient.invalidateQueries('assignments');
        queryClient.invalidateQueries('assignments-dashboard');
        queryClient.invalidateQueries('analytics-overview');
        queryClient.invalidateQueries('survey-analytics');
        queryClient.invalidateQueries('available-users');
        setShowManageDialog(false); setSelectedUser(null);
        setUserToDelete(null);
        toast.success('User deleted');
      },
      onError: (err: any) => toast.error(err.response?.data?.detail || 'Failed to delete user'),
    }
  );

  const { data: deletionImpact, isLoading: deletionImpactLoading } = useQuery(
    ['user-deletion-impact', userToDelete?.id],
    () => usersApi.getDeletionImpact(userToDelete!.id),
    { enabled: !!userToDelete, staleTime: 0, cacheTime: 0 }
  );

  const createAssignmentMutation = useMutation(assignmentsApi.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('assignments');
      setShowAssignDialog(false); setSelectedUserForAssignment(null); setSelectedSurveyForAssignment(null);
      toast.success('User assigned to survey');
    },
    onError: (error: any) => toast.error(error.response?.data?.detail || 'Failed to assign user'),
  });

  const handleCreateUser = async () => {
    if (!newUserFirstName.trim() || !newUserLastName.trim() || !newUserEmail.trim() || !newUserCountry.trim()) {
      toast.warning('Please fill in all required fields'); return;
    }
    setIsCreating(true);
    try {
      await createUserMutation.mutateAsync({
        first_name: newUserFirstName.trim(), last_name: newUserLastName.trim(),
        email: newUserEmail.trim(), country: newUserCountry.trim(), timezone: newUserTimezone,
      });
    } finally { setIsCreating(false); }
  };

  const bulkReminderMutation = useMutation(usersApi.bulkSetAutoReminder, {
    onSuccess: (res, vars) => {
      queryClient.invalidateQueries('users');
      const verb = vars.enabled ? 'on' : 'off';
      // Name the count that is actually a problem. Reporting how many CAN be
      // reached makes the admin subtract to find the number they care about.
      const stuck = res.updated - res.reachable;
      const warn = vars.enabled && stuck > 0
        ? ` — but ${stuck} of them can't be reached (no Telegram account, or not active)`
        : '';
      toast.success(`Reminder turned ${verb} for ${res.updated} user${res.updated === 1 ? '' : 's'}${warn}`);
      setSelectedIds([]);
    },
    onError: () => toast.error('Could not update the selected users'),
  });

  const applyBulkReminder = async (enabled: boolean) => {
    if (selectedIds.length === 0) return;
    setIsBulkUpdating(true);
    try {
      await bulkReminderMutation.mutateAsync({
        user_ids: selectedIds,
        enabled,
        // Only send a threshold when switching on; switching off shouldn't
        // silently rewrite thresholds an admin set per user.
        ...(enabled ? { days: Number(bulkDays) || 7 } : {}),
      });
    } finally { setIsBulkUpdating(false); }
  };

  // A marked user the bot cannot actually message is the failure worth showing:
  // the flag looks set, but nothing will ever be sent. Say so on the row rather
  // than only in the toast that appears once and disappears.
  const canReceive = (user: any) => user.status === 'ACTIVE' && !!user.telegram_user_code;

  const renderReminderCell = (user: any) => {
    if (!user.auto_reminder_enabled) {
      return <Text size={300} style={{ color: 'var(--color-text-tertiary)' }}>Off</Text>;
    }
    if (!canReceive(user)) {
      return (
        <Text
          size={300}
          // Not --color-warning: that token is #FDAD15, which as text on the
          // white row is far below a readable contrast ratio. This darker amber
          // carries the same meaning and can actually be read.
          style={{ color: '#B35900' }}
          title={
            !user.telegram_user_code
              ? "Marked, but this user has never linked Telegram — nothing can be sent until they run /register"
              : `Marked, but the user is ${user.status} — only ACTIVE users are messaged`
          }
        >
          On · {user.auto_reminder_days}d ⚠
        </Text>
      );
    }
    return (
      <Text size={300} style={{ color: 'var(--color-success)' }}>
        On · {user.auto_reminder_days}d
      </Text>
    );
  };

  const toggleOne = (id: number, checked: boolean) =>
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));

  const handleManageUser = (user: any) => {
    setSelectedUser(user);
    setEditUserFirstName(user.first_name); setEditUserLastName(user.last_name);
    setEditUserEmail(user.email); setEditUserCountry(user.country);
    setEditUserTimezone(user.timezone || 'UTC');
    setEditAutoReminder(!!user.auto_reminder_enabled);
    setEditAutoReminderDays(String(user.auto_reminder_days ?? 7));
    setShowManageDialog(true);
  };

  const handleUpdateUser = async () => {
    if (!editUserFirstName.trim() || !editUserLastName.trim() || !editUserEmail.trim() || !editUserCountry.trim()) {
      toast.warning('Please fill in all required fields'); return;
    }
    setIsUpdating(true);
    try {
      await updateUserMutation.mutateAsync({
        id: selectedUser.id,
        data: {
          first_name: editUserFirstName.trim(), last_name: editUserLastName.trim(),
          email: editUserEmail.trim(), country: editUserCountry.trim(), timezone: editUserTimezone,
          auto_reminder_enabled: editAutoReminder,
          auto_reminder_days: Number(editAutoReminderDays) || 7,
        },
      });
    } finally { setIsUpdating(false); }
  };

  const executeConfirmAction = async () => {
    if (!confirmAction) return;
    const { type, user } = confirmAction;
    if (type === 'deactivate') await updateUserStatusMutation.mutateAsync({ id: user.id, status: 'INACTIVE' });
    else if (type === 'reactivate') await updateUserStatusMutation.mutateAsync({ id: user.id, status: 'ACTIVE' });
    setConfirmAction(null);
  };

  const handleAssignUser = (user: any) => {
    setSelectedUserForAssignment(user);
    setShowAssignDialog(true);
    refetchAvailableSurveys();
  };

  const handleCreateAssignment = async () => {
    if (!selectedUserForAssignment || !selectedSurveyForAssignment) { toast.warning('Please select a survey'); return; }
    setIsAssigning(true);
    try {
      await createAssignmentMutation.mutateAsync({ user_id: selectedUserForAssignment.id, survey_id: selectedSurveyForAssignment });
    } finally { setIsAssigning(false); }
  };

  const botUsername: string = process.env.REACT_APP_TELEGRAM_BOT_USERNAME || '';

  const copyToClipboard = async (text: string) => {
    try { await navigator.clipboard.writeText(text); toast.success('Copied to clipboard'); }
    catch { toast.error('Copy failed'); }
  };

  const handleCopyCode = (code?: string | null) => {
    if (!code) return toast.warning('No registration code for this user.');
    copyToClipboard(code);
  };

  const handleCopyLink = (code?: string | null) => {
    if (!code) return toast.warning('No registration code for this user.');
    if (!botUsername) return toast.warning('Bot username is not configured.');
    copyToClipboard(`https://t.me/${botUsername}?start=${code}`);
  };

  return (
    <div>
      <PageHeader
        title="User Management"
        subtitle="Manage Telegram users and registration codes"
        actions={
          <Dialog open={showAddDialog} onOpenChange={(_, data) => setShowAddDialog(data.open)}>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="primary" icon={<Add24Regular />}>Add User</Button>
            </DialogTrigger>
            <DialogSurface style={{ width: '90vw', maxWidth: '480px' }}>
              <DialogBody>
                <DialogTitle>Create New User</DialogTitle>
                <DialogContent>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                    <Field label="First Name" required><Input value={newUserFirstName} onChange={(e) => setNewUserFirstName(e.target.value)} placeholder="Enter first name" /></Field>
                    <Field label="Last Name" required><Input value={newUserLastName} onChange={(e) => setNewUserLastName(e.target.value)} placeholder="Enter last name" /></Field>
                    <Field label="Email" required><Input value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} placeholder="Enter email address" type="email" /></Field>
                    <Field label="Country" required><Input value={newUserCountry} onChange={(e) => { setNewUserCountry(e.target.value); const tz = countryToTimezone(e.target.value); if (tz) setNewUserTimezone(tz); }} placeholder="Enter country" /></Field>
                    <Field label="Timezone" required hint="Used to send reminders at the user's local time">
                      <Select style={{ width: '100%' }} value={newUserTimezone} onChange={(_, data) => setNewUserTimezone(data.value)}>
                        {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                      </Select>
                    </Field>
                  </div>
                </DialogContent>
                <DialogActions>
                  <DialogTrigger disableButtonEnhancement><Button appearance="secondary">Cancel</Button></DialogTrigger>
                  <Button appearance="primary" onClick={handleCreateUser} disabled={isCreating || !newUserFirstName.trim() || !newUserLastName.trim() || !newUserEmail.trim() || !newUserCountry.trim()}>
                    {isCreating ? 'Creating...' : 'Create User'}
                  </Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
        }
      />

      <div className={s.card}>
        <div className={s.toolbar}>
          <div className={s.searchWrap}>
            <Search24Regular className={s.searchIcon} />
            <Input placeholder="Search users..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '36px', width: '100%' }} />
          </div>
          <div className={s.counters}>
            <div className={s.counter}>
              <span className={s.counterValue} style={{ color: 'var(--undp-primary)' }}>{totalUsers}</span>
              <span className={s.counterLabel}>Total</span>
            </div>
          </div>
        </div>

        {selectedIds.length > 0 && (
          <div className={s.bulkBar}>
            <Text size={300} weight="semibold">
              {selectedIds.length} selected
            </Text>
            <div className={s.bulkActions}>
              <Text size={200} style={{ color: 'var(--color-text-secondary)' }}>Remind after</Text>
              <Input
                type="number"
                min={1}
                max={365}
                value={bulkDays}
                onChange={(e) => setBulkDays(e.target.value)}
                style={{ width: '72px' }}
                aria-label="Days of silence before reminding"
              />
              <Text size={200} style={{ color: 'var(--color-text-secondary)' }}>days of silence</Text>
              <Button appearance="primary" disabled={isBulkUpdating} onClick={() => applyBulkReminder(true)}>
                {isBulkUpdating ? 'Saving...' : 'Turn reminder on'}
              </Button>
              <Button appearance="secondary" disabled={isBulkUpdating} onClick={() => applyBulkReminder(false)}>
                Turn off
              </Button>
              <Button appearance="subtle" onClick={() => setSelectedIds([])}>Clear</Button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-2xl)' }}><Spinner size="large" /></div>
        ) : users.length === 0 ? (
          <EmptyState
            icon={<Person24Regular />}
            title={debouncedSearch ? 'No users found' : 'No users yet'}
            description={debouncedSearch ? 'Try a different search term' : 'Add your first user to get started'}
          />
        ) : (
          <div className="table-scroll">
          <Table className={s.table}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell style={{ width: '44px' }}>
                  <Checkbox
                    checked={allOnPageSelected ? true : (selectedIds.length > 0 ? 'mixed' : false)}
                    onChange={(_, data) => setSelectedIds(data.checked ? users.map((u) => u.id) : [])}
                    aria-label="Select all users on this page"
                  />
                </TableHeaderCell>
                <TableHeaderCell style={{ width: '14%' }}>User</TableHeaderCell>
                <TableHeaderCell className={s.hideOnMobile} style={{ width: '17%' }}>Email</TableHeaderCell>
                <TableHeaderCell className={s.hideOnMobile} style={{ width: '11%' }}>Country</TableHeaderCell>
                <TableHeaderCell className={s.hideOnMobile} style={{ width: '11%' }}>Timezone</TableHeaderCell>
                <TableHeaderCell className={s.hideOnMobile} style={{ width: '14%' }}>Registration Code</TableHeaderCell>
                <TableHeaderCell style={{ width: '9%' }}>Status</TableHeaderCell>
                <TableHeaderCell className={s.hideOnMobile} style={{ width: '9%' }}>Reminder</TableHeaderCell>
                <TableHeaderCell className={s.hideOnMobile} style={{ width: '9%' }}>Joined</TableHeaderCell>
                <TableHeaderCell className={s.hideOnMobile} style={{ width: '72px' }}>Actions</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.includes(user.id)}
                      onChange={(_, data) => toggleOne(user.id, !!data.checked)}
                      aria-label={`Select ${user.first_name} ${user.last_name}`}
                    />
                  </TableCell>
                  <TableCell
                    className={s.nameCell}
                    onClick={isMobile ? () => setMobileUser(user) : undefined}
                  >
                    <Text weight="medium">{user.first_name} {user.last_name}</Text>
                    <span className={s.expandHint}>›</span>
                  </TableCell>
                  <TableCell className={s.hideOnMobile}><Text size={300}>{user.email}</Text></TableCell>
                  <TableCell className={s.hideOnMobile}><Text size={300}>{user.country}</Text></TableCell>
                  <TableCell className={s.hideOnMobile}><Text size={300}>{user.timezone || 'UTC'}</Text></TableCell>
                  <TableCell className={s.hideOnMobile}><Text size={300} style={{ fontFamily: 'var(--font-mono)', fontSize: '12px' }}>{user.telegram_registration_code || 'N/A'}</Text></TableCell>
                  <TableCell><StatusBadge status={user.status} /></TableCell>
                  <TableCell className={s.hideOnMobile}>{renderReminderCell(user)}</TableCell>
                  <TableCell className={s.hideOnMobile}><Text size={300}>{user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</Text></TableCell>
                  <TableCell className={s.hideOnMobile}>
                    <Menu>
                      <MenuTrigger disableButtonEnhancement>
                        <Button appearance="subtle" size="small" icon={<MoreHorizontal24Regular />} />
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          <MenuItem icon={<Edit24Regular />} onClick={() => handleManageUser(user)}>Edit User</MenuItem>
                          <MenuItem onClick={() => handleCopyCode(user.telegram_registration_code)}>Copy registration code</MenuItem>
                          <MenuItem onClick={() => handleCopyLink(user.telegram_registration_code)}>Copy Telegram link</MenuItem>
                          {user.status === 'ACTIVE' && <MenuItem icon={<DocumentText24Regular />} onClick={() => handleAssignUser(user)}>Assign to Survey</MenuItem>}
                          {user.status === 'ACTIVE' && <MenuItem icon={<PersonDelete24Regular />} onClick={() => setConfirmAction({ type: 'deactivate', user })}>Deactivate</MenuItem>}
                          {user.status === 'INACTIVE' && <MenuItem icon={<Person24Regular />} onClick={() => setConfirmAction({ type: 'reactivate', user })}>Reactivate</MenuItem>}
                          <MenuItem icon={<Delete24Regular />} onClick={() => setUserToDelete(user)}>Delete</MenuItem>
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        )}

        {!isLoading && totalUsers > 0 && (
          <Pagination
            total={totalUsers}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        )}
      </div>

      {/* Mobile-only user details popup */}
      {mobileUser && (
        <div className={s.modalOverlay} onClick={() => setMobileUser(null)}>
          <div className={s.modalPanel} onClick={(e) => e.stopPropagation()}>
            <div className={s.modalHeader}>
              <Text weight="semibold" size={400}>{mobileUser.first_name} {mobileUser.last_name}</Text>
              <Button appearance="subtle" size="small" icon={<Dismiss24Regular />} aria-label="Close" onClick={() => setMobileUser(null)} />
            </div>
            <div><StatusBadge status={mobileUser.status} /></div>
            <div className={s.detailMeta}>
              <span>Email: {mobileUser.email}</span>
              <span>Country: {mobileUser.country}</span>
              <span>Timezone: {mobileUser.timezone || 'UTC'}</span>
              <span style={{ fontFamily: 'var(--font-mono)' }}>Code: {mobileUser.telegram_registration_code || 'N/A'}</span>
              <span>Joined: {mobileUser.created_at ? new Date(mobileUser.created_at).toLocaleDateString() : 'N/A'}</span>
            </div>
            <div className={s.modalActions}>
              <Button appearance="primary" icon={<Edit24Regular />} onClick={() => { const u = mobileUser; setMobileUser(null); handleManageUser(u); }}>Edit User</Button>
              <Button appearance="secondary" onClick={() => handleCopyCode(mobileUser.telegram_registration_code)}>Copy registration code</Button>
              <Button appearance="secondary" onClick={() => handleCopyLink(mobileUser.telegram_registration_code)}>Copy Telegram link</Button>
              {mobileUser.status === 'ACTIVE' && <Button appearance="secondary" icon={<DocumentText24Regular />} onClick={() => { const u = mobileUser; setMobileUser(null); handleAssignUser(u); }}>Assign to Survey</Button>}
              {mobileUser.status === 'ACTIVE' && <Button appearance="secondary" icon={<PersonDelete24Regular />} onClick={() => { const u = mobileUser; setMobileUser(null); setConfirmAction({ type: 'deactivate', user: u }); }}>Deactivate</Button>}
              {mobileUser.status === 'INACTIVE' && <Button appearance="secondary" icon={<Person24Regular />} onClick={() => { const u = mobileUser; setMobileUser(null); setConfirmAction({ type: 'reactivate', user: u }); }}>Reactivate</Button>}
              <Button appearance="secondary" icon={<Delete24Regular />} onClick={() => { const u = mobileUser; setMobileUser(null); setUserToDelete(u); }} style={{ color: 'var(--color-error)' }}>Delete</Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Dialog */}
      <Dialog open={showManageDialog} onOpenChange={(_, data) => setShowManageDialog(data.open)}>
        <DialogSurface style={{ width: '90vw', maxWidth: '480px' }}>
          <DialogBody>
            <DialogTitle>Edit User</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                <Field label="First Name" required><Input value={editUserFirstName} onChange={(e) => setEditUserFirstName(e.target.value)} /></Field>
                <Field label="Last Name" required><Input value={editUserLastName} onChange={(e) => setEditUserLastName(e.target.value)} /></Field>
                <Field label="Email" required><Input value={editUserEmail} onChange={(e) => setEditUserEmail(e.target.value)} type="email" /></Field>
                <Field label="Country" required><Input value={editUserCountry} onChange={(e) => setEditUserCountry(e.target.value)} /></Field>
                <Field label="Timezone" required hint="Used to send reminders at the user's local time">
                  <Select style={{ width: '100%' }} value={editUserTimezone} onChange={(_, data) => setEditUserTimezone(data.value)}>
                    {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                  </Select>
                </Field>
                <Field
                  label="Automatic inactivity reminder"
                  hint="Only applies to users who haven't set their own reminder. Anyone who has one keeps it."
                >
                  <Switch
                    checked={editAutoReminder}
                    onChange={(_, data) => setEditAutoReminder(data.checked)}
                    label={editAutoReminder ? 'On' : 'Off'}
                  />
                </Field>
                {editAutoReminder && (
                  <Field
                    label="Remind after this many days of silence"
                    hint="Counted from the last message the user sent the bot. Repeats after the same gap if they stay quiet."
                  >
                    <Input
                      type="number"
                      min={1}
                      max={365}
                      value={editAutoReminderDays}
                      onChange={(e) => setEditAutoReminderDays(e.target.value)}
                    />
                  </Field>
                )}
                {selectedUser && (
                  <div className={s.infoBlock}>
                    <div className={s.infoRow}>Status: {selectedUser.status}</div>
                    <div className={s.infoRow}>Registration Code: {selectedUser.telegram_registration_code || 'N/A'}</div>
                    <div className={s.infoRow}>Telegram Code: {selectedUser.telegram_user_code || 'Not registered'}</div>
                    <div className={s.infoRow}>Created: {new Date(selectedUser.created_at).toLocaleDateString()}</div>
                  </div>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement><Button appearance="secondary">Cancel</Button></DialogTrigger>
              <Button appearance="primary" onClick={handleUpdateUser} disabled={isUpdating || !editUserFirstName.trim() || !editUserLastName.trim() || !editUserEmail.trim() || !editUserCountry.trim()}>
                {isUpdating ? 'Updating...' : 'Update User'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={(_, data) => setShowAssignDialog(data.open)}>
        <DialogSurface style={{ width: '90vw', maxWidth: '480px' }}>
          <DialogBody>
            <DialogTitle>Assign User to Survey</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                {selectedUserForAssignment && (
                  <div className={s.infoBlock}>
                    <Text size={300} weight="semibold">User: </Text>
                    <Text size={300}>{selectedUserForAssignment.first_name} {selectedUserForAssignment.last_name}</Text>
                  </div>
                )}
                <Field label="Select Survey" required>
                  <Select style={{ width: '100%' }} value={selectedSurveyForAssignment?.toString() || ''} onChange={(_, data) => setSelectedSurveyForAssignment(data.value ? parseInt(data.value) : null)}>
                    <Option value="" text="Choose a survey...">Choose a survey...</Option>
                    {availableSurveys?.map((survey) => (
                      <Option key={survey.id} value={survey.id.toString()} text={`${survey.title} (${survey.status})`}>{survey.title} ({survey.status})</Option>
                    ))}
                  </Select>
                  {availableSurveys && availableSurveys.length === 0 && (
                    <Text size={200} style={{ color: 'var(--color-error)', marginTop: '4px' }}>No surveys available for assignment.</Text>
                  )}
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement><Button appearance="secondary">Cancel</Button></DialogTrigger>
              <Button appearance="primary" onClick={handleCreateAssignment} disabled={isAssigning || !selectedSurveyForAssignment}>
                {isAssigning ? 'Assigning...' : 'Assign User'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Deactivate / Reactivate confirm */}
      <ConfirmDialog
        open={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={executeConfirmAction}
        title={confirmAction?.type === 'deactivate' ? 'Deactivate User' : 'Reactivate User'}
        message={`Are you sure you want to ${confirmAction?.type} ${confirmAction?.user?.first_name} ${confirmAction?.user?.last_name}?`}
        confirmLabel={confirmAction?.type === 'deactivate' ? 'Deactivate' : 'Reactivate'}
        danger={confirmAction?.type === 'deactivate'}
      />

      {/* Permanent delete */}
      <DeleteUserDialog
        open={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        onConfirm={() => userToDelete && deleteUserMutation.mutate(userToDelete.id)}
        impact={deletionImpact ?? null}
        impactLoading={deletionImpactLoading}
        loading={deleteUserMutation.isLoading}
      />
    </div>
  );
}
